import React from 'react';

import { DeleteIcon, FileIcon, PlusIcon } from '@vlabs/icons';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type {
  ArrayPath, Control, FieldArray, FieldValues, Path,
} from 'react-hook-form';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './AttachmentsInput.module.sass';

interface AttachmentsInputProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
}

function AttachmentsInput<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
}: AttachmentsInputProps<TFieldValues>): React.JSX.Element {
  const { t } = useTranslation();
  const fieldArray = useFieldArray({ name: name as ArrayPath<TFieldValues>, control });
  const attachments: File[] = useWatch({ control, name });
  const controlledFieldArray: {
    id: string;
    file?: File;
  }[] = fieldArray.fields.map((field, index) => ({
    ...field,
    file: attachments[index],
  }));

  const addAttachment = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;

    const handler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const isAlreadyAttached = (file: File): boolean => attachments !== undefined
        && attachments.length > 0
        && attachments.some((attachment) => (
          attachment.name === file.name
          && attachment.size === file.size
          && attachment.lastModified === file.lastModified
        ));
      Array.from(e.target.files ?? [])
        .filter((file) => !isAlreadyAttached(file))
        .forEach((file) => fieldArray.append(file as FieldArray<TFieldValues>));
    };
    input.onchange = handler;

    input.click();
  };

  return (
    <div className={st.Root}>
      {controlledFieldArray.map((field, fieldIdx) => {
        if (field.file === undefined) return undefined;
        return (
          <div
            className={st.Attachment}
            key={field.id}
          >
            <div className={st.AttachmentIcon}>
              <FileIcon />
            </div>
            <div className={st.AttachmentBody}>
              <div className={st.AttachmentBodyValue}>
                <div className={st.AttachmentName}>{field.file.name}</div>
                <div className={st.AttachmentClear}>
                  <IconButton
                    icon={<DeleteIcon />}
                    kind="negative"
                    onClick={() => fieldArray.remove(fieldIdx)}
                    variant="dimmed"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }).filter((item) => !!item)}

      <div>
        <Button
          icon={<PlusIcon />}
          kind="primary"
          onClick={addAttachment}
          variant="flat"
        >
          {t('features:createCard.btn.add_file')}
        </Button>
      </div>
    </div>
  );
}

export { AttachmentsInput };
